import { cs } from '../../lib/currency';
/**
 * Super Admin → Plans (iter309-redesign PR 5)
 *
 * Dependency-aware plan builder. Every feature list now round-trips
 * through the backend closure resolver so:
 *   1. Selecting a feature auto-includes its transitive deps.
 *   2. Save is blocked on cycles / missing keys / duplicates.
 *   3. Operator sees an Impact Preview (tenants affected + feature
 *      diff) before mutating an existing plan.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { toggleWithDeps } from "../../lib/featureDeps";

// API errors may carry a structured detail ({code, errors:[{message}]}); never hand an object to toast.
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  return d?.errors?.map((x) => x.message).filter(Boolean).join("; ") || d?.message || fallback;
};
import {
  Loader2,
  Save,
  Plus,
  X,
  AlertTriangle,
  Users,
  Package,
  Info,
  CheckCircle2,
  Table2,
} from "lucide-react";

const API =
  (typeof window !== "undefined" && window.location
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL) + "/api";

const authHeader = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const slugifyId = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

const priceLabel = (n) =>
  n === null || n === undefined || n === "" ? "TBD" : `${cs()}${Number(n).toFixed(2)}`;

export default function SuperAdminPlans() {
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  // Validation state driven by /plans/validate
  const [validation, setValidation] = useState(null); // {is_valid, direct_..., inherited_..., effective_..., errors, warnings, effective_limits}
  const [validating, setValidating] = useState(false);

  // Impact preview modal
  const [impact, setImpact] = useState(null); // {loading, data?, error?}

  // New-plan dialog
  const [creating, setCreating] = useState(false);
  const [newPlan, setNewPlan] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  // iter335f (#13) — archive/restore a plan. Backend guards against
  // archiving a plan that still has active tenants (409).
  const setPlanStatus = async (planId, status) => {
    try {
      await axios.patch(`${API}/super-admin/plans/${planId}`, { status }, { headers: authHeader() });
      toast.success(status === "archived" ? "Plan archived" : "Plan restored");
      load();
    } catch (e) {
      toast.error(errText(e, "Could not update plan status"));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, f] = await Promise.all([
        axios.get(`${API}/super-admin/plans/detailed`, { headers: authHeader() }),
        axios.get(`${API}/super-admin/features`, { headers: authHeader() }),
      ]);
      setPlans(p.data.plans || []);
      setFeatures(f.data.features || []);
    } catch (e) {
      toast.error(errText(e, "Failed to load"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openPlan = (planId) => {
    const p = plans.find((x) => x.id === planId);
    if (!p) return;
    setSelected(planId);
    setDraft({
      ...p,
      // Use direct_feature_keys (the operator's picks) — the effective set
      // is derived server-side on validate.
      included_feature_keys: [...(p.direct_feature_keys || p.included_feature_keys || [])],
    });
  };

  // Re-validate whenever the draft's feature list changes (debounced).
  useEffect(() => {
    if (!draft) {
      setValidation(null);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setValidating(true);
      try {
        const res = await axios.post(
          `${API}/super-admin/plans/validate`,
          {
            included_feature_keys: draft.included_feature_keys,
            limit_overrides: draft.limit_overrides || {},
          },
          { headers: authHeader(), signal: controller.signal },
        );
        setValidation(res.data);
      } catch (e) {
        if (!axios.isCancel(e)) {
          setValidation(null);
        }
      } finally {
        setValidating(false);
      }
    }, 220);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [draft?.included_feature_keys?.join("|"), draft?.limit_overrides]);

  const applyToggle = (keys, key) => {
    const r = toggleWithDeps(keys, key, features);
    if (r.added.length) toast.info(`Also turned on (required): ${r.added.join(", ")}`);
    if (r.removed?.length > 1) {
      toast.info(`Also turned off (depends on this): ${r.removed.filter((item) => item !== key).join(", ")}`);
    }
    return r.keys;
  };

  const toggleFeature = (key) => {
    const keys = applyToggle(draft.included_feature_keys, key);
    if (keys) setDraft({ ...draft, included_feature_keys: keys });
  };

  const openImpactPreview = async () => {
    if (!selected || !draft) return;
    setImpact({ loading: true });
    try {
      const res = await axios.post(
        `${API}/super-admin/plans/${selected}/impact-preview`,
        {
          included_feature_keys: draft.included_feature_keys,
          limit_overrides: draft.limit_overrides || {},
        },
        { headers: authHeader() },
      );
      setImpact({ loading: false, data: res.data });
    } catch (e) {
      setImpact({
        loading: false,
        error: e.response?.data?.detail || "Failed to compute impact",
      });
    }
  };

  const confirmSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/super-admin/plans/${selected}`,
        {
          name: draft.name,
          description: draft.description,
          price_per_month: draft.price_per_month,
          price_per_year: draft.price_per_year,
          included_feature_keys: draft.included_feature_keys,
        },
        { headers: authHeader() },
      );
      toast.success("Plan saved");
      setImpact(null);
      await load();
    } catch (e) {
      toast.error(errText(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  // ---- new plan ----
  const openNewPlan = () => {
    setNewPlan({
      id: "",
      name: "",
      tier: "custom",
      description: "",
      price_per_month: null,
      price_per_year: null,
      included_feature_keys: [],
    });
  };

  const toggleNewPlanFeature = (key) => {
    if (!newPlan) return;
    const keys = applyToggle(newPlan.included_feature_keys, key);
    if (keys) setNewPlan({ ...newPlan, included_feature_keys: keys });
  };

  const createPlan = async () => {
    if (!newPlan) return;
    const id = (newPlan.id || slugifyId(newPlan.name)).trim();
    const name = (newPlan.name || "").trim();
    if (!id || !name) {
      toast.error("Plan id and name are required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(
        `${API}/super-admin/plans`,
        {
          id,
          name,
          tier: newPlan.tier || "custom",
          description: newPlan.description || null,
          price_per_month: newPlan.price_per_month,
          price_per_year: newPlan.price_per_year,
          included_feature_keys: newPlan.included_feature_keys || [],
          limit_overrides: {},
        },
        { headers: authHeader() },
      );
      toast.success(`Plan "${name}" created`);
      setNewPlan(null);
      await load();
      setSelected(id);
    } catch (e) {
      toast.error(errText(e, "Failed to create plan"));
    } finally {
      setCreating(false);
    }
  };

  const byCategory = useMemo(() => {
    const out = {};
    features.forEach((f) => {
      out[f.category] = out[f.category] || [];
      out[f.category].push(f);
    });
    return out;
  }, [features]);

  const inheritedSet = new Set(validation?.inherited_dependency_keys || []);
  const directSet = new Set(validation?.direct_feature_keys || draft?.included_feature_keys || []);
  const hasErrors = validation && !validation.is_valid;

  return (
    <div className="space-y-6" data-testid="super-admin-plans-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Plans</h1>
          <p className="text-sm text-slate-500">
            Bundle features. Dependencies auto-included; save is blocked on cycles or missing keys.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/super-admin/plans/compare"
            data-testid="plans-compare-btn"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-sm font-medium px-3.5 py-1.5 rounded"
          >
            <Table2 className="w-4 h-4" /> Compare plans
          </a>
          <button
            type="button"
            onClick={openNewPlan}
            data-testid="plans-new-btn"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3.5 py-1.5 rounded"
          >
            <Plus className="w-4 h-4" /> New plan
          </button>
        </div>
      </header>

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
          <aside
            className="bg-white border border-slate-200 rounded-lg p-2 space-y-1"
            data-testid="plans-sidebar"
          >
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-500 cursor-pointer border-b border-slate-100 mb-1">
              <input
                type="checkbox"
                data-testid="plans-show-archived"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived plans
            </label>
            {plans.filter((p) => showArchived || p.status !== "archived").map((p) => (
              <button
                key={p.id}
                data-testid={`plan-tab-${p.id}`}
                onClick={() => openPlan(p.id)}
                className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                  selected === p.id ? "bg-slate-900 text-white" : "hover:bg-slate-100"
                } ${p.status === "archived" ? "opacity-60" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">
                    {p.name}
                    {p.status === "archived" && <span className="ml-1.5 text-[10px] uppercase tracking-wide">(archived)</span>}
                  </span>
                  {p.has_dependency_issue && (
                    <span
                      title="This plan has missing dependencies"
                      className="inline-flex items-center"
                    >
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                    </span>
                  )}
                </div>
                <div className="text-xs opacity-70 flex items-center gap-2 mt-0.5">
                  <span>{priceLabel(p.price_per_month)}/mo</span>
                  <span>·</span>
                  <span>
                    {(p.direct_feature_keys || []).length} direct · {(p.inherited_dependency_keys || []).length} inh
                  </span>
                  {p.tenant_count > 0 && (
                    <span
                      className={`ml-auto inline-flex items-center gap-1 ${
                        selected === p.id ? "text-slate-200" : "text-slate-500"
                      }`}
                    >
                      <Users className="w-3 h-3" />
                      {p.tenant_count}
                    </span>
                  )}
                </div>
                <div className="mt-1">
                  {p.status === "archived" ? (
                    <span
                      role="button"
                      data-testid={`plan-restore-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); setPlanStatus(p.id, "active"); }}
                      className={`text-[11px] underline ${selected === p.id ? "text-slate-200" : "text-blue-600"}`}
                    >
                      Restore
                    </span>
                  ) : (
                    <span
                      role="button"
                      data-testid={`plan-archive-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); setPlanStatus(p.id, "archived"); }}
                      title={p.tenant_count > 0 ? "Transfer active tenants first" : "Archive this plan"}
                      className={`text-[11px] underline ${selected === p.id ? "text-slate-200" : "text-slate-400 hover:text-red-600"}`}
                    >
                      Archive
                    </span>
                  )}
                </div>
              </button>
            ))}
            {plans.length === 0 && (
              <p className="text-xs text-slate-400 px-3 py-2">
                No plans yet. Click &quot;New plan&quot;.
              </p>
            )}
          </aside>

          <section className="bg-white border border-slate-200 rounded-lg p-6">
            {!draft ? (
              <p className="text-slate-400 text-sm">Select a plan to edit.</p>
            ) : (
              <div className="space-y-5">
                {/* Plan header row */}
                <div className="grid grid-cols-3 gap-3">
                  <label className="text-sm">
                    Name{" "}
                    <input
                      data-testid="plan-edit-name"
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      className="w-full mt-1 border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="text-sm">
                    Price / month ($){" "}
                    <input
                      data-testid="plan-edit-price"
                      type="number"
                      value={draft.price_per_month ?? ""}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          price_per_month: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                      className="w-full mt-1 border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="text-sm">
                    Price / year ($){" "}
                    <input
                      data-testid="plan-edit-price-year"
                      type="number"
                      value={draft.price_per_year ?? ""}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          price_per_year: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                      className="w-full mt-1 border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                  </label>
                </div>

                <label className="text-sm block">
                  Description{" "}
                  <textarea
                    data-testid="plan-edit-desc"
                    value={draft.description || ""}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    rows={2}
                    className="w-full mt-1 border border-slate-300 rounded px-2 py-1 text-sm"
                  />
                </label>

                {/* Validation summary */}
                <div
                  className={`rounded border p-3 text-sm ${
                    hasErrors
                      ? "border-rose-300 bg-rose-50 text-rose-800"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                  }`}
                  data-testid="plan-validation-summary"
                >
                  {validating ? (
                    <span className="inline-flex items-center gap-2 text-slate-500">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Validating…
                    </span>
                  ) : validation ? (
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center gap-1 font-semibold">
                          {hasErrors ? (
                            <AlertTriangle className="w-4 h-4" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          )}
                          {hasErrors ? "Invalid" : "Valid"}
                        </span>
                        <span>
                          {directSet.size} direct · {inheritedSet.size} inherited ·{" "}
                          {validation.effective_feature_keys?.length ?? 0} effective
                        </span>
                      </div>
                      {(validation.errors || []).map((err) => (
                        <div key={err.code} data-testid={`plan-error-${err.code}`}>
                          <span className="font-mono text-xs">[{err.code}]</span> {err.message}
                        </div>
                      ))}
                      {(validation.warnings || []).map((w) => (
                        <div
                          key={w.code}
                          className="text-amber-700"
                          data-testid={`plan-warning-${w.code}`}
                        >
                          <span className="font-mono text-xs">[{w.code}]</span> {w.message}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span>Toggle a feature to see effective closure.</span>
                  )}
                </div>

                {/* Features */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm">
                      Features ({draft.included_feature_keys.length} direct of {features.length})
                    </h3>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" /> Direct
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded-sm bg-sky-400" /> Inherited
                      </span>
                    </div>
                  </div>
                  <div
                    className="space-y-3 max-h-[440px] overflow-auto border border-slate-200 rounded p-3"
                    data-testid="plan-feature-list"
                  >
                    {Object.entries(byCategory).map(([cat, feats]) => (
                      <div key={cat}>
                        <div className="text-xs uppercase font-semibold text-slate-500 mb-1">
                          {cat}
                        </div>
                        {feats.map((f) => {
                          const isDirect = directSet.has(f.key);
                          const isInherited = inheritedSet.has(f.key);
                          return (
                            <label
                              key={f.key}
                              className={`flex items-start gap-2 py-1 text-sm rounded px-1 ${
                                isInherited && !isDirect ? "bg-sky-50/60" : ""
                              }`}
                              data-testid={`plan-feature-row-${f.key}`}
                            >
                              <input
                                type="checkbox"
                                data-testid={`plan-feature-${f.key}`}
                                checked={isDirect}
                                onChange={() => toggleFeature(f.key)}
                                disabled={f.always_on}
                                className="mt-1"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium flex items-center gap-1.5 flex-wrap">
                                  {f.name}
                                  {f.always_on && (
                                    <span className="text-[10px] uppercase tracking-wide text-slate-400">
                                      always on
                                    </span>
                                  )}
                                  {isInherited && !isDirect && (
                                    <span
                                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-sky-100 text-sky-700 rounded px-1.5 py-0.5"
                                      data-testid={`plan-feature-inherited-${f.key}`}
                                    >
                                      inherited
                                    </span>
                                  )}
                                  {f.depends_on?.length > 0 && (
                                    <span className="text-[10px] text-slate-400">
                                      ← {f.depends_on.join(", ")}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500">{f.description}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Effective limits (read-only) */}
                {validation?.effective_limits &&
                  Object.keys(validation.effective_limits).length > 0 && (
                    <div className="rounded border border-slate-200 p-3">
                      <div className="text-xs font-semibold text-slate-600 uppercase mb-2 flex items-center gap-1">
                        <Package className="w-3.5 h-3.5" /> Effective limits
                      </div>
                      <div className="flex flex-wrap gap-1.5" data-testid="plan-effective-limits">
                        {Object.entries(validation.effective_limits).map(([k, v]) => (
                          <span
                            key={k}
                            className="inline-flex items-center gap-1 text-xs bg-slate-100 border border-slate-200 rounded px-2 py-0.5 font-mono"
                          >
                            {k}: <b>{v === null ? "∞" : String(v)}</b>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid="plan-preview-impact-btn"
                    onClick={openImpactPreview}
                    disabled={saving || hasErrors}
                    className="bg-white border border-slate-300 text-slate-700 rounded px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Info className="w-4 h-4" />
                    Preview impact & save
                  </button>
                  {hasErrors && (
                    <span className="text-xs text-rose-600" data-testid="plan-save-blocked">
                      Fix validation errors above to save.
                    </span>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {/* Impact Preview modal */}
      {impact && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
          data-testid="plan-impact-dialog"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <Info className="w-5 h-5 text-slate-500" /> Impact preview
              </h2>
              <button
                type="button"
                onClick={() => setImpact(null)}
                data-testid="plan-impact-close-btn"
                className="text-slate-400 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {impact.loading && (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Computing…
                </div>
              )}
              {impact.error && (
                <div className="text-sm text-rose-700">{impact.error}</div>
              )}
              {impact.data && (
                <>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="border border-slate-200 rounded p-3">
                      <div className="text-xs text-slate-500 uppercase">Tenants affected</div>
                      <div
                        className="text-2xl font-semibold mt-1"
                        data-testid="impact-tenant-count"
                      >
                        {impact.data.tenants_affected}
                      </div>
                    </div>
                    <div className="border border-emerald-200 bg-emerald-50 rounded p-3">
                      <div className="text-xs text-emerald-700 uppercase">Features added</div>
                      <div
                        className="text-2xl font-semibold text-emerald-700 mt-1"
                        data-testid="impact-added-count"
                      >
                        +{impact.data.features.added_count}
                      </div>
                    </div>
                    <div className="border border-rose-200 bg-rose-50 rounded p-3">
                      <div className="text-xs text-rose-700 uppercase">Features removed</div>
                      <div
                        className="text-2xl font-semibold text-rose-700 mt-1"
                        data-testid="impact-removed-count"
                      >
                        −{impact.data.features.removed_count}
                      </div>
                    </div>
                  </div>

                  {(impact.data.features.added?.length > 0 ||
                    impact.data.features.removed?.length > 0) && (
                    <div className="text-xs space-y-1.5 max-h-32 overflow-auto">
                      {impact.data.features.added?.length > 0 && (
                        <div>
                          <span className="text-emerald-700 font-semibold">Added:</span>{" "}
                          <span className="font-mono text-slate-600">
                            {impact.data.features.added.join(", ")}
                          </span>
                        </div>
                      )}
                      {impact.data.features.removed?.length > 0 && (
                        <div>
                          <span className="text-rose-700 font-semibold">Removed:</span>{" "}
                          <span className="font-mono text-slate-600">
                            {impact.data.features.removed.join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {impact.data.limits?.changed_count > 0 && (
                    <div className="text-xs">
                      <div className="font-semibold text-slate-600 mb-1">
                        {impact.data.limits.changed_count} limit change(s):
                      </div>
                      <div className="space-y-0.5 max-h-24 overflow-auto">
                        {impact.data.limits.diff.map((d) => (
                          <div key={d.key} className="font-mono text-slate-600">
                            {d.key}: {String(d.old ?? "∞")} → {String(d.new ?? "∞")}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {impact.data.tenants_affected > 0 && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      This change affects {impact.data.tenants_affected} live tenant
                      {impact.data.tenants_affected === 1 ? "" : "s"}. Feature caches will
                      refresh within 60 seconds.
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setImpact(null)}
                disabled={saving}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSave}
                disabled={saving || !impact.data || !impact.data.validation?.is_valid}
                data-testid="plan-impact-confirm-btn"
                className="px-4 py-1.5 text-sm bg-slate-900 hover:bg-slate-800 text-white rounded inline-flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Confirm & save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Plan dialog */}
      {newPlan && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
          data-testid="new-plan-dialog"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h2 className="font-semibold text-lg">Create new plan</h2>
              <button
                type="button"
                onClick={() => setNewPlan(null)}
                data-testid="new-plan-close-btn"
                className="text-slate-400 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 overflow-auto space-y-4 flex-1">
              <p className="text-xs text-slate-500">
                Dependencies are auto-included at save time — pick only the top-level features you
                want to bill for.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm block">
                  Plan name
                  <input
                    value={newPlan.name}
                    onChange={(e) =>
                      setNewPlan({
                        ...newPlan,
                        name: e.target.value,
                        id: newPlan.id || slugifyId(e.target.value),
                      })
                    }
                    data-testid="new-plan-name-input"
                    placeholder="Lane Bookings Only"
                    className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="text-sm block">
                  Plan id (URL-safe)
                  <input
                    value={newPlan.id}
                    onChange={(e) => setNewPlan({ ...newPlan, id: slugifyId(e.target.value) })}
                    data-testid="new-plan-id-input"
                    placeholder="lane_bookings_only"
                    className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm font-mono"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm block">
                  Price / month ($)
                  <input
                    type="number"
                    value={newPlan.price_per_month ?? ""}
                    onChange={(e) =>
                      setNewPlan({
                        ...newPlan,
                        price_per_month: e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                    data-testid="new-plan-price-month-input"
                    className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="text-sm block">
                  Price / year ($)
                  <input
                    type="number"
                    value={newPlan.price_per_year ?? ""}
                    onChange={(e) =>
                      setNewPlan({
                        ...newPlan,
                        price_per_year: e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                    data-testid="new-plan-price-year-input"
                    className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                  />
                </label>
              </div>

              <label className="text-sm block">
                Description
                <textarea
                  value={newPlan.description || ""}
                  onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                  rows={2}
                  data-testid="new-plan-desc-input"
                  className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                />
              </label>

              <div>
                <h3 className="font-semibold text-sm mb-2">
                  Included features ({newPlan.included_feature_keys.length} of {features.length})
                </h3>
                <div className="space-y-3 max-h-[280px] overflow-auto border border-slate-200 rounded p-3">
                  {Object.entries(byCategory).map(([cat, feats]) => (
                    <div key={cat}>
                      <div className="text-xs uppercase font-semibold text-slate-500 mb-1">
                        {cat}
                      </div>
                      {feats.map((f) => (
                        <label
                          key={f.key}
                          className="flex items-start gap-2 py-1 text-sm cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={newPlan.included_feature_keys.includes(f.key)}
                            onChange={() => toggleNewPlanFeature(f.key)}
                            disabled={f.always_on}
                            data-testid={`new-plan-feature-${f.key}`}
                            className="mt-1"
                          />
                          <div>
                            <div className="font-medium">
                              {f.name}{" "}
                              {f.always_on && (
                                <span className="text-xs text-slate-400">(always on)</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500">{f.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setNewPlan(null)}
                disabled={creating}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={createPlan}
                disabled={creating || !newPlan.name?.trim()}
                data-testid="new-plan-create-btn"
                className="px-4 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded inline-flex items-center gap-2 disabled:opacity-50"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Create plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
